import {
  policySearchV2Mapping,
  policyServiceMappings,
} from './policyServiceMappings';
import { quoteServiceMappings } from '../inbox/quoteServiceMappings';
import { ProductTypes } from '../types';
import { applyQueryConfig, makeCancelable, ServiceHandler } from '../utils';
import { standardQuerySerializer } from '../api';
import {
  getPrimeXPaymentDetails,
  getPrimeXPolicyDetailsByView,
} from '../api/policies.api';
import type { CreateBorChangeRequestDto } from '../api/policies.api.types';

const policyParamsSerializer = (params: any) =>
  standardQuerySerializer(applyQueryConfig(policyServiceMappings, params));

const fetchPreviousPolicyMainDataHandler = ServiceHandler({
  url: `/api/policy/v1/P250/prevpolicy/:policyId`,
  method: 'get',
});
export const fetchPreviousPolicyMainData = ({
  policyId,
}: {
  policyId: string | number;
}) => fetchPreviousPolicyMainDataHandler(policyId);

const fetchOriginalPolicyDataHandler = ServiceHandler({
  url: 'api/policy/v1/:product/endorsement/original/:policyNumber',
  method: 'get',
});
export const fetchOriginalPolicyData = ({ product, policyNumber }: any) =>
  fetchOriginalPolicyDataHandler(product, policyNumber);

const fetchPoliciesHandler = makeCancelable(
  ServiceHandler({
    url: '/api/policy/v1/search',
    paramsSerializer: policyParamsSerializer,
  })
);
export const fetchPolicies = (params: Record<string, unknown> = {}) =>
  fetchPoliciesHandler({ params });

const fetchEndorsementPoliciesHandler = makeCancelable(
  ServiceHandler({
    url: '/api/policy/v1/search/endorsements',
    paramsSerializer: policyParamsSerializer,
  })
);
export const fetchEndorsementPolicies = (
  params: Record<string, unknown> = {}
) => fetchEndorsementPoliciesHandler({ params });

const fetchPolicyPremiumHandler = makeCancelable(
  ServiceHandler({
    url: 'api/policy/v1/search/premium/',
    paramsSerializer: policyParamsSerializer,
  })
);
export const fetchPolicyPremium = (params: Record<string, unknown> = {}) =>
  fetchPolicyPremiumHandler({ params });

const fetchAllPolicyPremiumHandler = makeCancelable(
  ServiceHandler({
    url: 'api/policy/v1/search/all-premium/',
    paramsSerializer: policyParamsSerializer,
  })
);
export const fetchAllPolicyPremium = (params: Record<string, unknown> = {}) =>
  fetchAllPolicyPremiumHandler({ params });

const fetchPolicyCapsHandler = ServiceHandler({
  url: 'api/policy/v1/search/caps/p100',
  paramsSerializer: policyParamsSerializer,
});
export const fetchPolicyCaps = (params: Record<string, unknown> = {}) =>
  fetchPolicyCapsHandler({ params });

const fetchPolicyLimitHandler = ServiceHandler({
  url: 'api/policy/v1/search/limit/',
  paramsSerializer: policyParamsSerializer,
});
export const fetchPolicyLimit = (params: Record<string, unknown> = {}) =>
  fetchPolicyLimitHandler({ params });

const fetchEndorsementPolicyPremiumStatsHandler = makeCancelable(
  ServiceHandler({
    url: 'api/policy/v1/search/endorsement-premium',
    paramsSerializer: policyParamsSerializer,
  })
);
export const fetchEndorsementPolicyPremiumStats = (
  params: Record<string, unknown> = {}
) => fetchEndorsementPolicyPremiumStatsHandler({ params });

/**
 * New location src/api/policies.api.ts
 * @deprecated
 */
const fetchLatestPolicyDataHandler = ServiceHandler({
  url: 'api/policy/v1/:product/endorsement/latest/:policyNumber',
  method: 'get',
});
export const fetchLatestPolicyData = ({ product, policyNumber }: any) =>
  fetchLatestPolicyDataHandler(product, policyNumber);

const sendPolicyCertificateEmailHandler = ServiceHandler({
  url: '/api/policy/v1/certificate/:productType',
  method: 'post',
});
export const sendPolicyCertificateEmail = (
  { productType }: { productType: string },
  data?: unknown
) => sendPolicyCertificateEmailHandler(productType, { data });

const createRenewalOptoutHandler = ServiceHandler({
  url: '/api/policy/v1/optout/:policyId',
  method: 'post',
});
export const createRenewalOptout = (
  { policyId }: { policyId: string | number },
  data?: unknown
) => createRenewalOptoutHandler(policyId, { data });

const updateRenewalOptoutHandler = ServiceHandler({
  url: '/api/policy/v1/optout/:policyId',
  method: 'put',
});
export const updateRenewalOptout = (
  { policyId }: { policyId: string | number },
  data?: unknown
) => updateRenewalOptoutHandler(policyId, { data });

const getRenewalOptoutReasonsHandler = makeCancelable(
  ServiceHandler({
    url: '/api/policy/v1/reasons',
    paramsSerializer: policyParamsSerializer,
  })
);
export const getRenewalOptoutReasons = (params: Record<string, unknown> = {}) =>
  getRenewalOptoutReasonsHandler({ params });

const updatePolicyNonPremiumDataHandler = ServiceHandler({
  url: '/api/policy/v1/nonpremium/:policyId',
  method: 'put',
});
export const updatePolicyNonPremiumData = (
  { policyId }: { policyId: string | number },
  data?: unknown
) => updatePolicyNonPremiumDataHandler(policyId, { data });

const updatePolicyEffEndDateHandler = ServiceHandler({
  url: '/api/policy/v1/nonpremium/:policyId/date',
  method: 'put',
});
export const updatePolicyEffEndDate = (
  { policyId }: { policyId: string | number },
  data?: unknown
) => updatePolicyEffEndDateHandler(policyId, { data });

const fetchAdditonalInsuredHandler = ServiceHandler({
  url: '/api/policy/v1/ai/:policyId',
});
export const fetchAdditonalInsured = ({
  policyId,
}: {
  policyId: string | number;
}) => fetchAdditonalInsuredHandler(policyId);

const fetchPolicyStatusesHandler = ServiceHandler({
  url: '/api/policy/v1/statuses',
});
export const fetchPolicyStatuses = (params: Record<string, unknown> = {}) =>
  fetchPolicyStatusesHandler({ params });

/**
 * New location src/apis/policies.api.ts
 * @deprecated
 */
const fetchPolicyDetailsHandler = ServiceHandler({
  url: '/api/policy/v1/:policyId',
});
export function fetchPolicyDetails(policyId: any) {
  return fetchPolicyDetailsHandler(policyId);
}

const fetchPolicyEndorsementDetailsHandler = ServiceHandler({
  url: 'api/policy/v1/endorsement/latest/:policyNum',
});
export function fetchPolicyEndorsementDetails(policyNum: any) {
  return fetchPolicyEndorsementDetailsHandler(policyNum);
}

/**
 * New location src/apis/policies.api.ts
 * @deprecated
 */
const fetch250PolicyDetailsHandler = ServiceHandler({
  url: '/api/policy/v1/P250/:policyId',
});
export function fetch250PolicyDetails(policyId: any) {
  return fetch250PolicyDetailsHandler(policyId);
}

const fetch250PolicyPageDetailsHandler = ServiceHandler({
  url: '/api/policy/v1/P250/details/:policyId',
});
export function fetch250PolicyPageDetails(policyId: any) {
  return fetch250PolicyPageDetailsHandler(policyId);
}

/**
 * New location src/apis/policies.api.ts
 * @deprecated
 */
export function getPolicyDetails(policyId: any, product: any, params: any) {
  if (product === ProductTypes.p100) {
    return fetchPolicyDetails(policyId);
  }
  if (product === ProductTypes.p250) {
    return fetch250PolicyPageDetails(policyId);
  }
  return getPrimeXPolicyDetailsByView(product, policyId, { params });
}

const regeneratePolicyDocP100Handler = ServiceHandler({
  url: '/api/policy/v1/document/:id',
});
const regeneratePolicyDocPxHandler = ServiceHandler({
  url: '/api/policy/v1/policyDoc/:id',
  method: 'put',
});
export function regeneratePolicyDocs({ id, product }: any) {
  if (product === ProductTypes.p100) {
    return regeneratePolicyDocP100Handler(id);
  }
  return regeneratePolicyDocPxHandler(id);
}

export function getPolicyDetailsWithFirmo(policyId: any, product: any) {
  if (product === ProductTypes.p100) {
    return fetchPolicyDetails(policyId);
  }
  if (product === ProductTypes.p250) {
    return fetch250PolicyDetails(policyId);
  }
  return getPrimeXPaymentDetails(product, policyId);
}

const unReadPolicyHandler = ServiceHandler({
  url: '/api/policy/v1/search/read',
  paramsSerializer: policyParamsSerializer,
});
export const unReadPolicy = (params: Record<string, unknown> = {}) =>
  unReadPolicyHandler({ params });

const deletePolicyHandler = ServiceHandler({
  url: '/api/policy/v1/:policyNumber',
  method: 'delete',
});
export function deletePolicy(policyNumber: any) {
  return deletePolicyHandler(policyNumber);
}

const downloadPolicyDocumentsHandler = ServiceHandler({
  url: '/api/docs/v1/policy/:accountId/:policyId',
});
export function downloadPolicyDocuments(
  accountId: any,
  policyId: any,
  created: any,
  isEndorsement: any,
  product: any,
  policyNumber: any
) {
  return downloadPolicyDocumentsHandler(accountId, policyId, {
    params: { createdDate: created, isEndorsement, product, policyNumber },
  });
}

const downloadPolicyCertificateHandler = ServiceHandler({
  url: '/api/docs/v1/policy/:accountId/certificate/:policyId',
});
export function downloadPolicyCertificate(
  accountId: any,
  policyId: any,
  created: any
) {
  return downloadPolicyCertificateHandler(accountId, policyId, {
    params: { createdDate: created },
  });
}

const downloadReceiptHandler = ServiceHandler({ url: '/api/docs/v1/receipt' });
export function downloadReceipt(
  accountId: any,
  policyId: any,
  created: any,
  isEndorsement: any
) {
  return downloadReceiptHandler({
    params: { accountId, policyId, createdDate: created, isEndorsement },
  });
}

const downloadPaymentPlanHandler = ServiceHandler({
  url: '/api/billing/v1/finance',
});
export function downloadPaymentPlan(
  accountId: any,
  policyId: any,
  created: any,
  isEndorsement: any
) {
  return downloadPaymentPlanHandler({
    params: { accountId, policyId, createdDate: created, isEndorsement },
  });
}

const voidPolicyHandler = ServiceHandler({
  url: '/api/policy/v1/:productType/void/:policyId',
  method: 'put',
});
export function voidPolicy({ policyId, productType }: any, details: any) {
  return voidPolicyHandler(productType, policyId, { data: details });
}

const cancelP100PolicyHandler = ServiceHandler({
  url: '/api/policy/v1/cancel/:policyId',
  method: 'put',
});
export function cancelP100Policy(policyId: any, details: any) {
  return cancelP100PolicyHandler(policyId, { data: details });
}

const cancelPrimeXPolicyHandler = ServiceHandler({
  url: `/api/policy/v1/:productType/cancel/:policyId`,
  method: 'put',
});
export const cancelPrimeXPolicy = (
  { productType, policyId }: any,
  data?: unknown
) => cancelPrimeXPolicyHandler(productType, policyId, { data });

const cancelP250PolicyHandler = ServiceHandler({
  url: '/api/policy/v1/P250/cancel/:id',
  method: 'put',
});
export const cancelP250Policy = (
  { id }: { id: string | number },
  data?: unknown
) => cancelP250PolicyHandler(id, { data });

const cancelP250ExcessPolicyHandler = ServiceHandler({
  url: '/api/policy/v1/P250/excess/cancel/:id',
  method: 'put',
});
export const cancelP250ExcessPolicy = (
  { id }: { id: string | number },
  data?: unknown
) => cancelP250ExcessPolicyHandler(id, { data });

const downloadInvoiceHandler = ServiceHandler({ url: '/api/docs/v1/invoice' });
export function downloadInvoice(
  accountId: any,
  policyId: any,
  created: any,
  latest: any,
  isEndorsement: any
) {
  return downloadInvoiceHandler({
    params: {
      accountId,
      policyId,
      createdDate: created,
      latest,
      isEndorsement,
    },
  });
}

const downloadAWSDocHandler = ServiceHandler({ url: '' });
export function downloadAWSDoc(url: any) {
  return downloadAWSDocHandler({ url, responseType: 'blob' });
}

const changeAgencyBilledHandler = ServiceHandler({
  url: 'api/policy/v1/toggle/agencyBilled',
  method: 'put',
});
export function changeAgencyBilled(policyId: any, product: any) {
  return changeAgencyBilledHandler({ params: { policyId, product } });
}

const togglePaymentRemindersHandler = ServiceHandler({
  url: ' api/billing/v1/toggle/invoiceReminder',
  method: 'put',
});
export function togglePaymentReminders(policyId: any) {
  return togglePaymentRemindersHandler({ params: { policyId } });
}

const markCheckInMailHandler = ServiceHandler({
  url: 'api/policy/v1/description/:policyId',
  method: 'put',
});
export function markCheckInMail(policyId: any) {
  return markCheckInMailHandler(policyId);
}

export const changeBOR = ServiceHandler({
  url: '/api/policy/v1/bor/:policyId',
  method: 'post',
});
export const expediteBOR = ServiceHandler({
  url: '/api/policy/v1/update/BOR/:id',
  method: 'put',
});

export const expediteBorX = ServiceHandler({
  url: '/api/policy/v1/bor/expedite/:policyNumber',
  method: 'put',
});

export const rescindBOR = ServiceHandler({
  url: '/api/policy/v1/bor/:policyId',
  method: 'put',
});

export const rescindBorX = ServiceHandler({
  url: '/api/policy/v1/bor/rescind/:policyNumber/:docId',
  method: 'put',
});

export const createBorChangeRequest = ServiceHandler<CreateBorChangeRequestDto>(
  {
    url: '/api/policy/v1/bor/change-request',
    method: 'post',
  }
);

// Not cancelable: the book dashboard asks for the current and prior period at once.
const getBinderPolicyStatsHandler = ServiceHandler({
  url: '/api/policy/v1/search/bound-premium',
  paramsSerializer: (p) =>
    standardQuerySerializer(applyQueryConfig(quoteServiceMappings, p)),
});
export const getBinderPolicyStats = (params: Record<string, unknown> = {}) =>
  getBinderPolicyStatsHandler({ params });

const fetchP250MidtermEndorsementsListHandler = makeCancelable(
  ServiceHandler({ url: '/api/policy/v1/P250/midterm/list/:policyNumber' })
);
const fetchP250MidtermEndorsementsList = ({ policyNumber }: any) =>
  fetchP250MidtermEndorsementsListHandler(policyNumber);

const fetchP100MidtermEndorsementsListHandler = makeCancelable(
  ServiceHandler({ url: '/api/policy/v1/midterm/list/:policyNumber' })
);
const fetchP100MidtermEndorsementsList = ({ policyNumber }: any) =>
  fetchP100MidtermEndorsementsListHandler(policyNumber);

const fetchPxMidtermEndorsementsList = ServiceHandler({
  url: '/api/policy/v1/:productType/midterm/list/:policynumber',
});

export const fetchMidtermEndorsementsList = ({
  policyNumber,
  productType,
}: any) => {
  if (productType === ProductTypes.p100) {
    return fetchP100MidtermEndorsementsList({ policyNumber });
  }
  if (productType === ProductTypes.p250) {
    return fetchP250MidtermEndorsementsList({ policyNumber });
  }

  return fetchPxMidtermEndorsementsList(productType, policyNumber);
};

const getPolicyAmountPaidHandler = ServiceHandler({
  url: '/api/billing/v1/amountPaid/:policyId',
});
export const getPolicyAmountPaid = ({
  policyId,
}: {
  policyId: string | number;
}) => getPolicyAmountPaidHandler(policyId);

const putP250FlatCancelHandler = ServiceHandler({
  url: '/api/policy/v1/P250/flatcancel/:policyId',
  method: 'put',
});
export const putP250FlatCancel = (
  { policyId }: { policyId: string | number },
  data?: unknown
) => putP250FlatCancelHandler(policyId, { data });

const generateLossRunsHandler = ServiceHandler({
  url: '/api/policy/v1/lossRuns',
  method: 'post',
});
export const generateLossRuns = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => generateLossRunsHandler({ data });

const getLossRunsHandler = makeCancelable(
  ServiceHandler({ url: '/api/docs/v1/policy/:accountId/loss-runs/:policyId' })
);
export const getLossRuns = ({ accountId, policyId }: any) =>
  getLossRunsHandler(accountId, policyId);

const getAllCancellationReasonsHandler = ServiceHandler({
  url: '/api/policy/v1/noc/reason',
});
export const getAllCancellationReasons = (
  params: Record<string, unknown> = {}
) => getAllCancellationReasonsHandler({ params });

const erpDurationHandler = ServiceHandler({
  url: '/api/policy/v1/P250/erp',
  method: 'post',
});
export const erpDuration = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => erpDurationHandler({ data });

const getErpPremiumHandler = ServiceHandler({
  url: '/api/policy/v1/P250/erp/premium/:policyId',
});
export const getErpPremium = ({ policyId, ...rest }: any) =>
  getErpPremiumHandler(policyId, { params: rest });

export const getErpPremiumV2 = ServiceHandler({
  url: '/api/policy/v1/erp/premium/:policyId',
  method: 'post',
});

export const getErpEligibility = ServiceHandler({
  url: '/api/policy/v1/erp/eligibility/:policyId',
  method: 'get',
});

export const getErpMonthOptions = ServiceHandler({
  url: '/api/policy/v1/erp/options/:product',
  method: 'get',
});

export const issueErpPolicy = ServiceHandler({
  url: '/api/policy/v1/erp/issue/:policyId',
  method: 'post',
});

const muteRenewalRemindersHandler = ServiceHandler({
  url: '/api/policy/v1/:policyId/mute-renewal-reminders/:product',
  method: 'post',
});
export const muteRenewalReminders = ({ policyId, product }: any) =>
  muteRenewalRemindersHandler(policyId, product);

const checkProductEligibilityForQuotingHandler = ServiceHandler({
  url: '/api/policy/v1/product-eligibility',
  method: 'post',
});
export const checkProductEligibilityForQuoting = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => checkProductEligibilityForQuotingHandler({ data });

const reinstateCancelledP100Policy = ServiceHandler({
  url: '/api/policy/v1/undo-cancelled-policy/:policyId',
  method: 'PUT',
});

const reinstateCancelledP250Policy = ServiceHandler({
  url: '/api/policy/v1/P250/undo-cancelled-policy/:policyId',
  method: 'PUT',
});

export const reinstateCancelledPolicy = (policy: any) => {
  const { product: productType, id } = policy;
  switch (productType) {
    case ProductTypes.p100:
      return reinstateCancelledP100Policy(id);
    case ProductTypes.p250:
      return reinstateCancelledP250Policy(id);
    default:
  }
};

export const searchPoliciesV2 = ServiceHandler({
  url: '/api/policy/v1/search/dashboard',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(policySearchV2Mapping, params)),
});
